export interface GSplatParticlePhaseWeights {
  particleize: number
  scatter: number
  gather: number
  restore: number
}

export interface GSplatParticleLifecycleTimingInput {
  duration?: number
  preScatterHoldDuration?: number
  peakHoldDuration?: number
  phaseWeights?: Partial<GSplatParticlePhaseWeights>
}

export interface GSplatParticleLifecycleTiming {
  duration: number
  preScatterHoldDuration: number
  peakHoldDuration: number
  phaseWeights: GSplatParticlePhaseWeights
  phaseDurations: GSplatParticlePhaseWeights
}

export type GSplatParticleLifecyclePhase =
  | 'particleize'
  | 'pre-scatter-hold'
  | 'scatter'
  | 'peak-hold'
  | 'gather'
  | 'restore'
  | 'complete'

export interface GSplatParticleLifecycleProgress {
  phase: GSplatParticleLifecyclePhase
  elapsedTime: number
  normalizedTime: number
  particleMix: number
  scatterProgress: number
  complete: boolean
}

export const DEFAULT_GSPLAT_PARTICLE_LIFECYCLE_DURATION = 6
export const DEFAULT_GSPLAT_PARTICLE_PRE_SCATTER_HOLD_DURATION = 0.5
export const DEFAULT_GSPLAT_PARTICLE_PEAK_HOLD_DURATION = 0.5
export const DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS: Readonly<GSplatParticlePhaseWeights> = {
  particleize: 1,
  scatter: 2,
  gather: 2,
  restore: 1,
}

export function normalizeGSplatParticleLifecycleTiming(
  input: GSplatParticleLifecycleTimingInput = {}
): GSplatParticleLifecycleTiming {
  const duration = readPositiveFinite(
    input.duration ?? DEFAULT_GSPLAT_PARTICLE_LIFECYCLE_DURATION,
    'duration'
  )
  const peakHoldDuration = readNonNegativeFinite(
    input.peakHoldDuration ?? DEFAULT_GSPLAT_PARTICLE_PEAK_HOLD_DURATION,
    'peakHoldDuration'
  )
  const preScatterHoldDuration = readNonNegativeFinite(
    input.preScatterHoldDuration ?? DEFAULT_GSPLAT_PARTICLE_PRE_SCATTER_HOLD_DURATION,
    'preScatterHoldDuration'
  )
  if (preScatterHoldDuration + peakHoldDuration >= duration) {
    throw new RangeError('preScatterHoldDuration + peakHoldDuration must be less than duration')
  }

  const phaseWeights = {
    particleize: readNonNegativeFinite(
      input.phaseWeights?.particleize ?? DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS.particleize,
      'phaseWeights.particleize'
    ),
    scatter: readNonNegativeFinite(
      input.phaseWeights?.scatter ?? DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS.scatter,
      'phaseWeights.scatter'
    ),
    gather: readNonNegativeFinite(
      input.phaseWeights?.gather ?? DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS.gather,
      'phaseWeights.gather'
    ),
    restore: readNonNegativeFinite(
      input.phaseWeights?.restore ?? DEFAULT_GSPLAT_PARTICLE_PHASE_WEIGHTS.restore,
      'phaseWeights.restore'
    ),
  }
  const weightTotal =
    phaseWeights.particleize + phaseWeights.scatter + phaseWeights.gather + phaseWeights.restore
  if (weightTotal <= 0) {
    throw new RangeError('phaseWeights must contain at least one positive value')
  }
  const weightedDuration = duration - preScatterHoldDuration - peakHoldDuration
  const phaseDurations = {
    particleize: (weightedDuration * phaseWeights.particleize) / weightTotal,
    scatter: (weightedDuration * phaseWeights.scatter) / weightTotal,
    gather: (weightedDuration * phaseWeights.gather) / weightTotal,
    restore: (weightedDuration * phaseWeights.restore) / weightTotal,
  }

  return {
    duration,
    preScatterHoldDuration,
    peakHoldDuration,
    phaseWeights,
    phaseDurations,
  }
}

export function resolveGSplatParticleLifecycleProgress(
  elapsedTime: number,
  timingInput: GSplatParticleLifecycleTiming | GSplatParticleLifecycleTimingInput = {}
): GSplatParticleLifecycleProgress {
  if (!Number.isFinite(elapsedTime) || elapsedTime < 0) {
    throw new RangeError('elapsedTime must be a non-negative finite number')
  }

  const timing = isNormalizedTiming(timingInput)
    ? timingInput
    : normalizeGSplatParticleLifecycleTiming(timingInput)
  const clampedTime = Math.min(elapsedTime, timing.duration)
  const normalizedTime = clampedTime / timing.duration
  const particleizeEnd = timing.phaseDurations.particleize
  const preScatterHoldEnd = particleizeEnd + timing.preScatterHoldDuration
  const scatterEnd = preScatterHoldEnd + timing.phaseDurations.scatter
  const holdEnd = scatterEnd + timing.peakHoldDuration
  const gatherEnd = holdEnd + timing.phaseDurations.gather

  if (clampedTime >= timing.duration) {
    return createProgress('complete', clampedTime, normalizedTime, 0, 0, true)
  }
  if (clampedTime < particleizeEnd) {
    return createProgress(
      'particleize',
      clampedTime,
      normalizedTime,
      easeInOut(clampedTime / particleizeEnd),
      0,
      false
    )
  }
  if (timing.preScatterHoldDuration > 0 && clampedTime < preScatterHoldEnd) {
    return createProgress('pre-scatter-hold', clampedTime, normalizedTime, 1, 0, false)
  }
  if (clampedTime < scatterEnd) {
    return createProgress(
      'scatter',
      clampedTime,
      normalizedTime,
      1,
      easeInOut((clampedTime - preScatterHoldEnd) / timing.phaseDurations.scatter),
      false
    )
  }
  if (timing.peakHoldDuration > 0 && clampedTime < holdEnd) {
    return createProgress('peak-hold', clampedTime, normalizedTime, 1, 1, false)
  }
  if (clampedTime < gatherEnd) {
    return createProgress(
      'gather',
      clampedTime,
      normalizedTime,
      1,
      1 - easeInOut((clampedTime - holdEnd) / timing.phaseDurations.gather),
      false
    )
  }

  return createProgress(
    'restore',
    clampedTime,
    normalizedTime,
    1 - easeInOut((clampedTime - gatherEnd) / timing.phaseDurations.restore),
    0,
    false
  )
}

function createProgress(
  phase: GSplatParticleLifecyclePhase,
  elapsedTime: number,
  normalizedTime: number,
  particleMix: number,
  scatterProgress: number,
  complete: boolean
): GSplatParticleLifecycleProgress {
  return {
    phase,
    elapsedTime,
    normalizedTime,
    particleMix: clamp01(particleMix),
    scatterProgress: clamp01(scatterProgress),
    complete,
  }
}

function isNormalizedTiming(
  value: GSplatParticleLifecycleTiming | GSplatParticleLifecycleTimingInput
): value is GSplatParticleLifecycleTiming {
  return 'phaseDurations' in value
}

function easeInOut(value: number): number {
  const t = clamp01(value)
  return t * t * (3 - 2 * t)
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

function readPositiveFinite(value: number, path: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${path} must be a positive finite number`)
  }
  return value
}

function readNonNegativeFinite(value: number, path: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${path} must be a non-negative finite number`)
  }
  return value
}
